// iter-137.j · Admin Voice Preview + preference widget.
// Lists available Web Speech API voices, lets admins pin a Hindi + English
// voice, tune rate/pitch/volume, and hear a live preview before showtime.
// Selections persist in localStorage so the wall kiosk announcer
// (`announceCheckinSuccess`) picks them up automatically.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Loader2, Volume2, RotateCcw, Save, Mic } from "lucide-react";
import { announceCheckinSuccess, listVoicesAsync, speak, unlockAudio } from "../lib/notify";

const LS_KEYS = {
  hi: "efc.voice.hi",
  en: "efc.voice.en",
  rate: "efc.voice.rate",
  pitch: "efc.voice.pitch",
  volume: "efc.voice.volume",
  // iter-138.p — voice gender preference: "female" | "male" | ""(any).
  gender: "efc.voice.gender",
  // iter-140 — announcement language: "en" | "hi" | "both".
  language: "efc.voice.language",
};

// Rough heuristic — Web Speech API doesn't expose gender directly. Names
// beginning with these terms are commonly female-tagged voices across
// Chrome/Edge/Windows/Android engines. Anything else is treated as male
// (or unknown). Admin can always pin an exact voice below to override.
const _FEMALE_HINTS = /female|woman|zira|aditi|shreya|veena|geeta|hema|prisha|kalpana|neha|ipsheeta|mahita|niha|swara|hina|kajal|meera|priya|payal|sonal|isha|ananya|neel/i;
const _MALE_HINTS = /male|man|david|ravi|hemant|arjun|sagar|amit|karan|rahul|kabir|vijay|akshay|prabhat/i;
export function voiceGender(voice) {
  if (!voice || !voice.name) return "unknown";
  if (_FEMALE_HINTS.test(voice.name)) return "female";
  if (_MALE_HINTS.test(voice.name)) return "male";
  return "unknown";
}

function readLS(key, fallback = "") {
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}

export default function AdminVoiceSettings() {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hindiVoice, setHindiVoice] = useState(readLS(LS_KEYS.hi));
  const [englishVoice, setEnglishVoice] = useState(readLS(LS_KEYS.en));
  const [rate, setRate] = useState(Number(readLS(LS_KEYS.rate)) || 1.0);
  const [pitch, setPitch] = useState(Number(readLS(LS_KEYS.pitch)) || 1.0);
  const [volume, setVolume] = useState(Number(readLS(LS_KEYS.volume)) || 1.0);
  const [gender, setGender] = useState(readLS(LS_KEYS.gender, "") || "");
  const [language, setLanguage] = useState(readLS(LS_KEYS.language, "en") || "en");
  const [testName, setTestName] = useState("Rushikesh");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listVoicesAsync();
      setVoices(list);
      // Auto-pick sensible defaults if nothing chosen yet.
      if (!hindiVoice) {
        const hi = list.find((v) => v.lang === "hi-IN") || list.find((v) => /hindi|veena/i.test(v.name));
        if (hi) setHindiVoice(hi.name);
      }
      if (!englishVoice) {
        const en = list.find((v) => v.lang === "en-IN") || list.find((v) => /india|rishi/i.test(v.name));
        if (en) setEnglishVoice(en.name);
      }
    } catch (e) {
      toast.error("Failed to load voices");
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const hindiVoices = useMemo(() => voices.filter((v) => /^hi/i.test(v.lang) || /hindi|veena/i.test(v.name)), [voices]);
  const englishVoices = useMemo(() => voices.filter((v) => /^en/i.test(v.lang)), [voices]);

  const save = () => {
    try {
      localStorage.setItem(LS_KEYS.hi, hindiVoice || "");
      localStorage.setItem(LS_KEYS.en, englishVoice || "");
      localStorage.setItem(LS_KEYS.rate, String(rate));
      localStorage.setItem(LS_KEYS.pitch, String(pitch));
      localStorage.setItem(LS_KEYS.volume, String(volume));
      localStorage.setItem(LS_KEYS.gender, gender || "");
      localStorage.setItem(LS_KEYS.language, language || "en");
      toast.success("Voice preferences saved · wall kiosk will use these instantly");
    } catch { toast.error("Save failed"); }
  };

  const reset = () => {
    setHindiVoice(""); setEnglishVoice("");
    setRate(1.0); setPitch(1.0); setVolume(1.0);
    setGender("");
    setLanguage("en");
    try {
      Object.values(LS_KEYS).forEach((k) => localStorage.removeItem(k));
      toast.success("Reset to browser defaults");
    } catch { /* noop */ }
  };

  // iter-138.p — when the admin picks a gender, auto-select the first
  // matching Hindi + English voice so the "Save" button has sensible
  // defaults ready. Admin can still override with the dropdowns below.
  const applyGender = (g) => {
    setGender(g);
    if (!g) return;
    const matches = (v) => voiceGender(v) === g;
    const hi = hindiVoices.find(matches);
    const en = englishVoices.find(matches);
    if (hi) setHindiVoice(hi.name);
    if (en) setEnglishVoice(en.name);
  };

  const previewLine = (lang, text) => {
    unlockAudio();
    speak(text, {
      lang,
      voice: lang.startsWith("hi") ? (hindiVoice || undefined) : (englishVoice || undefined),
      rate, pitch, volume,
    });
  };

  const previewFull = (meal) => {
    // Snapshot current UI values into localStorage first so the announcer
    // picks them up even before "Save" is tapped.
    try {
      localStorage.setItem(LS_KEYS.hi, hindiVoice || "");
      localStorage.setItem(LS_KEYS.en, englishVoice || "");
      localStorage.setItem(LS_KEYS.rate, String(rate));
      localStorage.setItem(LS_KEYS.pitch, String(pitch));
      localStorage.setItem(LS_KEYS.volume, String(volume));
      localStorage.setItem(LS_KEYS.language, language || "en");
    } catch { /* noop */ }
    unlockAudio();
    announceCheckinSuccess(meal, testName);
  };

  return (
    <div className="max-w-3xl mx-auto p-6 sm:p-8 space-y-6" data-testid="admin-voice-settings">
      <div>
        <p className="text-xs tracking-overline uppercase font-bold text-secondary">Wall kiosk</p>
        <h1 className="font-display font-extrabold text-3xl mt-1 flex items-center gap-2">
          <Volume2 className="h-7 w-7 text-primary" /> Voice preview & tuning
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pick the wall kiosk’s announcement language, pin the exact Hindi / English voice, tune rate, pitch and volume, and preview the check-in phrase before service starts.
        </p>
      </div>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading voices…
        </div>
      ) : (
        <>
          <div className="rounded-2xl card-3d p-5 space-y-4">
            <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground">Voice selection</p>

            {/* iter-140 — announcement language toggle. The wall kiosk
                previously spoke Hindi + English back-to-back which staff
                found confusing. Admins can now pick ONE language (or opt
                into the legacy dual-language mode). */}
            <div>
              <span className="text-xs tracking-overline uppercase font-bold text-muted-foreground">Announcement language</span>
              <div className="flex gap-2 mt-1.5" role="radiogroup" aria-label="Announcement language">
                {[
                  { id: "en", label: "English" },
                  { id: "hi", label: "Hindi" },
                  { id: "both", label: "Both" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={language === opt.id}
                    onClick={() => setLanguage(opt.id)}
                    data-testid={`voice-language-${opt.id}`}
                    className={`flex-1 h-10 rounded-xl border-2 font-extrabold text-xs uppercase tracking-[0.14em] transition-colors ${
                      language === opt.id
                        ? "border-primary bg-primary text-white shadow-sm"
                        : "border-black/10 bg-background text-foreground hover:border-primary/40"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Wall kiosk speaks one utterance per scan in the chosen language. Pick “Both” to keep the legacy Hindi-then-English behaviour.
              </p>
            </div>

            {/* iter-138.p — quick female / male toggle. Applies a heuristic to
                the voice name and pre-selects a matching Hindi + English
                voice below; admin can still override in the dropdowns. */}
            <div>
              <span className="text-xs tracking-overline uppercase font-bold text-muted-foreground">Preferred voice gender</span>
              <div className="flex gap-2 mt-1.5" role="radiogroup" aria-label="Preferred voice gender">
                {[
                  { id: "female", label: "Female voice" },
                  { id: "male", label: "Male voice" },
                  { id: "", label: "Any" },
                ].map((opt) => (
                  <button
                    key={opt.id || "any"}
                    type="button"
                    role="radio"
                    aria-checked={gender === opt.id}
                    onClick={() => applyGender(opt.id)}
                    data-testid={`voice-gender-${opt.id || "any"}`}
                    className={`flex-1 h-10 rounded-xl border-2 font-extrabold text-xs uppercase tracking-[0.14em] transition-colors ${
                      gender === opt.id
                        ? "border-primary bg-primary text-white shadow-sm"
                        : "border-black/10 bg-background text-foreground hover:border-primary/40"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="text-xs tracking-overline uppercase font-bold text-muted-foreground">Hindi voice</span>
              <div className="flex gap-2 mt-1">
                <select
                  value={hindiVoice}
                  onChange={(e) => setHindiVoice(e.target.value)}
                  className="flex-1 h-10 rounded-xl border border-input bg-background px-3 text-sm"
                  data-testid="voice-hindi-select"
                >
                  <option value="">Browser default</option>
                  {hindiVoices.map((v) => (
                    <option key={v.name + v.lang} value={v.name}>{v.name} · {v.lang}</option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => previewLine("hi-IN", `${testName || "अतिथि"}, लंच चेक-इन सफल हुआ। धन्यवाद।`)}
                  data-testid="voice-hindi-preview"
                >
                  <Mic className="h-4 w-4 mr-1.5" /> Test
                </Button>
              </div>
              {hindiVoices.length === 0 && (
                <p className="text-[11px] text-amber-700 mt-1">No Hindi voice found on this device. On Windows: Settings → Time &amp; Language → Speech → add Hindi voice pack. On Android/Chromebook, install &quot;Google Text-to-Speech · Hindi&quot;.</p>
              )}
            </label>

            <label className="block">
              <span className="text-xs tracking-overline uppercase font-bold text-muted-foreground">English voice</span>
              <div className="flex gap-2 mt-1">
                <select
                  value={englishVoice}
                  onChange={(e) => setEnglishVoice(e.target.value)}
                  className="flex-1 h-10 rounded-xl border border-input bg-background px-3 text-sm"
                  data-testid="voice-english-select"
                >
                  <option value="">Browser default</option>
                  {englishVoices.map((v) => (
                    <option key={v.name + v.lang} value={v.name}>{v.name} · {v.lang}</option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => previewLine("en-IN", `${testName || "Guest"}, lunch check-in successful. Thank you.`)}
                  data-testid="voice-english-preview"
                >
                  <Mic className="h-4 w-4 mr-1.5" /> Test
                </Button>
              </div>
            </label>
          </div>

          <div className="rounded-2xl card-3d p-5 space-y-3">
            <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground">Rate · Pitch · Volume</p>
            <Slider label="Rate" min={0.5} max={2.0} step={0.05} value={rate} onChange={setRate} suffix="×" testid="voice-rate" />
            <Slider label="Pitch" min={0.5} max={2.0} step={0.05} value={pitch} onChange={setPitch} suffix="×" testid="voice-pitch" />
            <Slider label="Volume" min={0.1} max={1.0} step={0.05} value={volume} onChange={setVolume} suffix="×" testid="voice-volume" />
          </div>

          <div className="rounded-2xl card-3d p-5 space-y-3">
            <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground">Full preview · check-in flow</p>
            <div className="flex gap-2 items-center flex-wrap">
              <label className="text-xs font-bold text-muted-foreground">Subscriber name:</label>
              <Input
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                placeholder="e.g. Rushikesh"
                className="flex-1 min-w-[180px] max-w-[280px] rounded-xl h-9"
                data-testid="voice-test-name"
              />
              <Button
                onClick={() => previewFull("lunch")}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold"
                data-testid="voice-preview-lunch"
              >
                <Volume2 className="h-4 w-4 mr-1.5" /> Play lunch check-in
              </Button>
              <Button
                onClick={() => previewFull("dinner")}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold"
                data-testid="voice-preview-dinner"
              >
                <Volume2 className="h-4 w-4 mr-1.5" /> Play dinner check-in
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Plays the success chord + the announcement in your chosen language(s) — exactly what the wall kiosk will say when this subscriber taps in.
            </p>
          </div>

          <div className="flex items-center justify-between gap-3">
            <Button variant="outline" onClick={reset} className="rounded-full" data-testid="voice-reset">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Reset to defaults
            </Button>
            <Button onClick={save} className="rounded-full" data-testid="voice-save">
              <Save className="h-4 w-4 mr-1.5" /> Save preferences
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function Slider({ label, min, max, step, value, onChange, suffix, testid }) {
  return (
    <label className="block" data-testid={`${testid}-wrap`}>
      <div className="flex items-center justify-between text-xs">
        <span className="tracking-overline uppercase font-bold text-muted-foreground">{label}</span>
        <span className="tabular-nums font-mono font-bold text-foreground">{Number(value).toFixed(2)}{suffix || ""}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-1 accent-primary"
        data-testid={testid}
      />
    </label>
  );
}
